import type { ICustomerFulfillment } from "@/data/model/Customer";
import { FulfillmentPositionDisplayMode, FulfillmentProductionState, FulfillmentState } from "@/data/model/Enums";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";

import { config } from "@/app/App";
import { trans } from "@/app/localization/Trans";

export class CustomerFulfillmentHelper {
    constructor() {}

    static colorsMap: object;

    static remainingFulfillmentTime(fulfillment: ICustomerFulfillment): number {
        return fulfillment.effectiveReadyDateTime.getTime() - new Date().getTime();
    }

    static getFulfillmentProductionState(fulfillment: ICustomerFulfillment): FulfillmentProductionState {
        if (fulfillment.isFulfilled) return FulfillmentProductionState.FulfilledNotPaid;

        if (
            fulfillment.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Position &&
            fulfillment.state == FulfillmentState.InPreparation
        ) {
            if (
                fulfillment.currentQueuePosition != undefined &&
                fulfillment.currentQueuePosition <= config.fulfillmentOrangePositionThreshold
            )
                return FulfillmentProductionState.ReadyShortly;

            return FulfillmentProductionState.InPreparation;
        } else {
            const timeLeft = this.remainingFulfillmentTime(fulfillment);
            if (timeLeft <= 0) return FulfillmentProductionState.Ready;

            if (timeLeft <= config.fulfillmentOrangeTimeTreshold) return FulfillmentProductionState.ReadyShortly;

            return FulfillmentProductionState.InPreparation;
        }
    }

    static isReady(fulfillment: ICustomerFulfillment): boolean {
        const productionState = this.getFulfillmentProductionState(fulfillment);
        return productionState == FulfillmentProductionState.Ready;
    }

    static isInPreparation(fulfillment: ICustomerFulfillment): boolean {
        const productionState = this.getFulfillmentProductionState(fulfillment);
        return (
            productionState == FulfillmentProductionState.InPreparation ||
            productionState == FulfillmentProductionState.ReadyShortly
        );
    }

    static isPreparationState(state: FulfillmentProductionState): boolean {
        return state == FulfillmentProductionState.InPreparation || state == FulfillmentProductionState.ReadyShortly;
    }

    static newestFirstComparator(firstModel: ICustomerFulfillment, secondModel: ICustomerFulfillment): number {
        let diff = 0;
        const firstModelState = this.getFulfillmentProductionState(firstModel);
        const secondModelState = this.getFulfillmentProductionState(secondModel);

        // compare states - InPreparation = 0, Ready = 2
        diff = firstModelState - secondModelState;
        if (diff != 0) return diff;

        if (firstModelState == FulfillmentProductionState.InPreparation ||
            firstModelState == FulfillmentProductionState.ReadyShortly
        ) {
            // compare displays - Duration = 1, Position = 2
            diff = secondModel.fulfillmentPositionDisplayMode - firstModel.fulfillmentPositionDisplayMode;
            if (diff != 0) return diff;

            if (firstModel.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Position) {
                // compare positions - 1 - 3 - 3 in front
                diff = (secondModel.currentQueuePosition ?? Number.MAX_VALUE) - (firstModel.currentQueuePosition ?? Number.MAX_VALUE);
                if (diff != 0) return diff;

                // time diff 10:10 - 10:30 -> 10:30 in front
                diff = secondModel.orderDate.getTime() - firstModel.orderDate.getTime();
                if (diff != 0) return diff;

                // compare by fulfillment groups
                diff = -this.compareByFulfillmentGroups(firstModel, secondModel);
                return diff;
            }
        }

        // for all other cases - the newest first
        diff = secondModel.effectiveReadyDateTime.getTime() - firstModel.effectiveReadyDateTime.getTime();
        if (diff != 0) return diff;

        // compare by fulfillment groups
        diff = -this.compareByFulfillmentGroups(firstModel, secondModel);
        return diff;
    }

    static compareByFulfillmentGroups(firstModel: ICustomerFulfillment, secondModel: ICustomerFulfillment) {
        return this.compareFulfillmentGroups(firstModel, secondModel);
    }

    static compareFulfillmentGroups(
        firstFulfillmentGroup: ICustomerFulfillment,
        secondFulfillmentGroup: ICustomerFulfillment
    ) {
        if (firstFulfillmentGroup == null && secondFulfillmentGroup == null) return 0;

        // move null value after not null value
        if (firstFulfillmentGroup == null) return 1;
        if (secondFulfillmentGroup == null) return -1;

        // compare order values
        const firstModelOrder = firstFulfillmentGroup != null ? firstFulfillmentGroup.fulfillmentGroupOrder : null;
        const secondModelOrder = secondFulfillmentGroup != null ? secondFulfillmentGroup.fulfillmentGroupOrder : null;

        // move null value after not null value
        if (firstModelOrder == null && secondModelOrder != null) return 1;
        if (secondModelOrder == null && firstModelOrder != null) return -1;

        // get order diff
        const diff = (firstModelOrder ?? 0) - (secondModelOrder ?? 0);
        if (diff != 0) return diff;

        // compare by group names
        const firstFulfillmentGroupName = this.getFulfillmentGroupName(firstFulfillmentGroup);
        const secondFulfillmentGroupName = this.getFulfillmentGroupName(secondFulfillmentGroup);

        return firstFulfillmentGroupName!.localeCompare(secondFulfillmentGroupName!);
    }

    static getFulfillmentGroupName(fulfillment: ICustomerFulfillment) {
        const name =
            fulfillment.name === undefined
                ? trans.fulfillmentPopover.emptyGroup
                : Format.translate(fulfillment.name, clientLanguage());
        return Format.htmlEscape(name);
    }

    static getQueueIndicatorValue(
        fulfillment: ICustomerFulfillment,
        productionState: FulfillmentProductionState
    ): string {
        // display queue position/time value
        if (this.shouldDisplayQueuePosition(fulfillment, productionState)) {
            if (!fulfillment.hideCurrentQueueIndicator) {
                const positionToDisplay =
                    fulfillment.currentQueuePosition! < 100
                        ? `${fulfillment.currentQueuePosition}`
                        : '99<span class="unit">+</span>';
                return positionToDisplay;
            } else {
                return "";
            }
        } else {
            const timeDif = CustomerFulfillmentHelper.remainingFulfillmentTime(fulfillment);
            return DateTime.secondsToFulfillmentTimeStr(timeDif);
        }
    }

    static shouldDisplayQueuePosition(fulfillment: ICustomerFulfillment, productionState: FulfillmentProductionState) {
        return (
            fulfillment.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Position &&
            (productionState == FulfillmentProductionState.InPreparation ||
                productionState == FulfillmentProductionState.ReadyShortly)
        );
    }

    static getIcon(fulfillment: ICustomerFulfillment, productionState: FulfillmentProductionState): string {
        const displayMode = fulfillment.fulfillmentPositionDisplayMode;
        if (displayMode == FulfillmentPositionDisplayMode.Position) {
            if (productionState == FulfillmentProductionState.InPreparation) return "fa-list-ol";
            if (productionState == FulfillmentProductionState.ReadyShortly) return "fa-list-ol";
        }

        if (productionState == FulfillmentProductionState.InPreparation) return "fa-hourglass-start";
        if (productionState == FulfillmentProductionState.ReadyShortly) return "fa-hourglass-half";

        // for ready and fulfilled
        return "fa-history";
    }

    static initColorsMap() {
        const colorsMap = {};
        colorsMap[FulfillmentProductionState.InPreparation] = "green";
        colorsMap[FulfillmentProductionState.ReadyShortly] = "orange";
        colorsMap[FulfillmentProductionState.Ready] = "red";
        colorsMap[FulfillmentProductionState.FulfilledNotPaid] = "red";

        this.colorsMap = colorsMap;
    }
}

CustomerFulfillmentHelper.initColorsMap();
